import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface HeaderProps {
  transparent?: boolean;
}

const Header = ({ transparent = false }: HeaderProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    if (transparent) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [transparent]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: 'トップ' },
    { path: '/about', label: '当院について' },
    { path: '/doctor', label: '院長紹介' },
    { path: '/access', label: 'アクセス' },
    { path: '/news', label: 'お知らせ' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white text-gray-900 shadow-sm">
      <nav className="px-6 lg:px-20 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <span className="text-[#007bbb] text-xl md:text-2xl font-bold whitespace-nowrap">
              おかやま整形外科
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-base font-medium transition-colors whitespace-nowrap hover:text-[#007bbb]"
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            <a
              href="tel:086-012-3456"
              className="flex items-center space-x-2 font-semibold transition-colors whitespace-nowrap text-[#007bbb] hover:text-[#006399]"
            >
              <i className="ri-phone-line text-lg"></i>
              <span>086-012-3456</span>
            </a>
            <Link
              to="/contact"
              className="bg-[#007bbb] text-white px-6 py-2.5 rounded-full font-medium hover:bg-[#006399] transition-colors whitespace-nowrap"
            >
              お問い合わせ
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            aria-label="メニュー"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-6 py-4 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="block py-2 text-base font-medium text-gray-900 hover:text-[#007bbb] transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-gray-200 space-y-3">
              <a
                href="tel:086-012-3456"
                className="flex items-center space-x-2 text-gray-900 font-semibold"
              >
                <i className="ri-phone-line text-lg"></i>
                <span>086-012-3456</span>
              </a>
              <Link
                to="/contact"
                className="block text-center bg-[#007bbb] text-white px-6 py-2.5 rounded-full font-medium whitespace-nowrap"
              >
                お問い合わせ
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
